import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

import {
  MetaDataAttributes,
  Session,
  SoftwarePurpose,
  SoftwareFilter,
  SoftwareFilterAttribute,
} from 'models';
import { createFilters } from 'utils/filtering';

import Button from 'components/UI/Button';
import SelectField, { OptionType } from 'components/UI/SelectField';
import InputField from 'components/UI/InputField';

import { Grid, ButtonGrid } from './styled';

type Props = {
  onFilter: (value: SoftwareFilter[]) => void;
  onClear: () => void;
  data: MetaDataAttributes;
  session: Session;
};

const SoftwareFilters: FC<Props> = ({ onFilter, onClear }) => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    // eslint-disable-next-line no-empty-pattern
    formState: {},
  } = useForm();

  // Watch
  const [
    watchPurpose,
    watchName,
    watchVersion,
    watchBrand,
    watchCategory,
    watchReleasedFor,
    watchBundleId,
  ] = watch([
    SoftwareFilterAttribute.PURPOSE,
    SoftwareFilterAttribute.NAME,
    SoftwareFilterAttribute.VERSION,
    SoftwareFilterAttribute.BRAND,
    SoftwareFilterAttribute.CATEGORY,
    SoftwareFilterAttribute.RELEASED_FOR,
    SoftwareFilterAttribute.BUNDLE_ID,
  ]);

  // Select option
  const onSelect = useCallback(
    (name: SoftwareFilterAttribute) => (value: string) => setValue(name, value),
    [setValue]
  );

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values) => {
      const filters = createFilters<SoftwareFilter>(values);
      if (filters.length) {
        onFilter(filters);
      }
    },
    [onFilter]
  );

  const onReset = () => {
    onClear();
    reset({
      name: '',
      version: '',
      brand: '',
      category: '',
      releasedFor: '',
      purpsoes: '',
    });
  };

  // Status
  const statusOptions: OptionType[] = [
    {
      key: 'AFTERSALE_SERVICE',
      label: 'AFTERSALE_SERVICE',
      value: SoftwarePurpose.AFTERSALE_SERVICE,
    },
    {
      key: 'ASSEMBLY',
      label: 'ASSEMBLY',
      value: SoftwarePurpose.ASSEMBLY,
    },
    {
      key: 'FOTA',
      label: 'FOTA',
      value: SoftwarePurpose.FOTA,
    },
    {
      key: 'MANUFACTURING',
      label: 'MANUFACTURING',
      value: SoftwarePurpose.MANUFACTURING,
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid>
        <InputField
          description="software.name"
          register={register('name')}
          defaultValue={watchName}
        />
        <InputField
          description="software.version"
          register={register('version')}
          defaultValue={watchVersion}
        />
        <InputField
          description="software.brand"
          register={register('brand')}
          defaultValue={watchBrand}
        />
        <InputField
          description="software.category"
          register={register('category')}
          defaultValue={watchCategory}
        />
        <InputField
          description="software.released_for"
          register={register('releasedFor')}
          defaultValue={watchReleasedFor}
        />
        <InputField
          description="software.bundle_id"
          register={register('bundleId')}
          defaultValue={watchBundleId}
        />
        <SelectField
          name={SoftwareFilterAttribute.PURPOSE}
          register={register(SoftwareFilterAttribute.PURPOSE)}
          onSelect={onSelect(SoftwareFilterAttribute.PURPOSE)}
          placeholder={intl.formatMessage({ id: 'select.placeholder' })}
          label={intl.formatMessage({ id: 'software.purposes' })}
          defaultValue={watchPurpose}
          options={statusOptions}
        />
      </Grid>

      <ButtonGrid>
        <Button
          backgroundColor="transparent"
          color="primary"
          onClick={onReset}
          marginRight
        >
          <FormattedMessage id="filters.btn_clear" />
        </Button>
        <Button submit>
          <FormattedMessage id="filters.btn_submit" />
        </Button>
      </ButtonGrid>
    </form>
  );
};

export default SoftwareFilters;
